import { useExport } from '@qualio/export-components';
import {
  QButton,
  QCheckbox,
  QFormControl,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QRadio,
  QRadioGroup,
  QStack,
  QText,
  useCurrentUser,
  useDisclosure,
} from '@qualio/ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { exportServiceClient } from '../../../api';
import { PageOrientation, QualioDocument } from '../../../api/model/document';
import { buildExportPayload } from './ExportComparisonPayload';

export const CompareVersionsExportButton = ({
  qualioDocument,
  compareDocument,
  html,
  showStrikethrough,
}: {
  qualioDocument: QualioDocument;
  compareDocument: QualioDocument;
  html: string;
  showStrikethrough: boolean;
}) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();
  const [orientation, setOrientation] = useState<PageOrientation>(
    PageOrientation.Portrait,
  );
  const [coverPageEnabled, setCoverPageEnabled] = useState(true);
  const [includeStrikethrough, setIncludeStrikethrough] =
    useState(showStrikethrough);

  const { trigger, isLoading } = useExport(exportServiceClient, {
    toastTitle: `Exporting ${qualioDocument.code} ${qualioDocument.title}`,
    'data-cy': 'compare-versions-export-toast',
  });

  const handleOpenModal = () => {
    setIncludeStrikethrough(showStrikethrough);
    openModal();
  };

  const handleExport = () => {
    trigger(
      buildExportPayload(
        orientation,
        coverPageEnabled,
        qualioDocument,
        compareDocument,
        html,
        currentUser.fullName,
        currentUser.companyName,
        includeStrikethrough,
      ),
    );
    closeModal();
  };

  return (
    <>
      <QButton
        variant="ghost"
        data-cy="compare-versions-export-button"
        isDisabled={isLoading}
        onClick={handleOpenModal}
      >
        {t('buttons.export')}
      </QButton>
      <QModal isOpen={isModalOpen} onClose={closeModal} size="md">
        <QModalHeader>
          <QText>{t('modals.exportDocument')}</QText>
        </QModalHeader>
        <QModalBody>
          <QStack spacing={6}>
            <QFormControl label={t('forms.orientation')}>
              <QRadioGroup
                value={orientation}
                onChange={(value: string) =>
                  setOrientation(value as PageOrientation)
                }
                data-cy="compare-versions-export-orientation-selection"
              >
                <QStack direction="row">
                  <QRadio
                    value={PageOrientation.Portrait}
                    data-cy="compare-versions-export-portrait-orientation"
                  >
                    {t('labels.portrait')}
                  </QRadio>
                  <QRadio
                    value={PageOrientation.Landscape}
                    data-cy="compare-versions-export-landscape-orientation"
                  >
                    {t('labels.landscape')}
                  </QRadio>
                </QStack>
              </QRadioGroup>
            </QFormControl>
            <QFormControl label={t('forms.coverPage')}>
              <QCheckbox
                isChecked={coverPageEnabled}
                onChange={(e) => setCoverPageEnabled(e.target.checked)}
                data-cy="compare-versions-export-coverpage-selection"
              >
                {t('forms.includeCoverPage')}
              </QCheckbox>
            </QFormControl>
            <QFormControl label={t('forms.strikethrough')}>
              <QCheckbox
                isChecked={includeStrikethrough}
                onChange={(e) => setIncludeStrikethrough(e.target.checked)}
                data-cy="compare-versions-export-strikethrough-selection"
              >
                {t('forms.includeStrikethrough')}
              </QCheckbox>
            </QFormControl>
          </QStack>
        </QModalBody>
        <QModalActions>
          <QButton variant="outline" onClick={closeModal}>
            {t('buttons.cancel')}
          </QButton>
          <QButton
            isLoading={isLoading}
            data-cy="compare-versions-export-submit-button"
            onClick={handleExport}
          >
            {t('buttons.export')}
          </QButton>
        </QModalActions>
      </QModal>
    </>
  );
};
